import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plug, Truck, CreditCard, Copy, CheckCircle2, Loader2, Save, ExternalLink, Sheet as SheetIcon, Download, BarChart3, Link2, KeyRound, RefreshCw, ShieldCheck, Building2, Wallet, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

import Sheets from "@/pages/Sheets";
import ExportPage from "@/pages/Export";
import Reports from "@/pages/Reports";
import BkRosterPanel from "@/pages/BkRosterPanel";
import BkCentresPanel from "@/pages/BkCentresPanel";

/* ------------------------------------------------------------------ */
/* Reusable "chatbot-style" swap-and-save card                        */
/* ------------------------------------------------------------------ */
function CopyBox({ value, testid }) {
  const copy = async () => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 text-xs font-mono break-all" data-testid={testid}>
      <span className="flex-1">{value || "—"}</span>
      <Button size="sm" variant="ghost" onClick={copy}><Copy size={12} /></Button>
    </div>
  );
}

function SwapField({ label, current, placeholder, value, onChange, secret, testid }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-[11px] uppercase text-slate-500">
        <KeyRound size={11} /> {label}
        {current && <span className="ml-auto font-mono normal-case text-[10px] bg-slate-100 rounded px-1.5 py-0.5">current: {current}</span>}
      </div>
      <Input type={secret ? "password" : "text"} placeholder={placeholder}
             value={value} onChange={(e) => onChange(e.target.value)}
             className="h-10 rounded-lg" data-testid={testid} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Razorpay                                                            */
/* ------------------------------------------------------------------ */
function RazorpayPanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ webhook_secret: "", key_id: "", key_secret: "" });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(async () => {
    try { setCfg((await api.get(`/integrations/razorpay`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const patch = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
    if (!Object.keys(patch).length) { toast.error("Nothing to save — paste a new value first"); return; }
    setBusy(true);
    try {
      await api.post(`/integrations/razorpay`, patch);
      toast.success("Razorpay updated ✓");
      setForm({ webhook_secret: "", key_id: "", key_secret: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const syncMethods = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/integrations/razorpay/sync-methods`, {});
      toast.success(`Synced ${data.updated} orders · scanned ${data.scanned} · no-txn ${data.no_txn} · failed ${data.failed}`, { duration: 8000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };
  if (!cfg) return <Card><CardContent className="p-4">Loading Razorpay…</CardContent></Card>;
  return (
    <div className="space-y-4">
      <Card><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <CreditCard className="text-emerald-700" />
          <h2 className="text-lg font-semibold">Razorpay Payments</h2>
          <Badge className={cfg.key_id_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{cfg.key_id_configured ? "Configured" : "Not configured"}</Badge>
          <a className="ml-auto text-xs text-primary underline flex items-center gap-1" href="https://dashboard.razorpay.com/app/webhooks" target="_blank" rel="noreferrer"><ExternalLink size={11} /> Razorpay dashboard</a>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2">
          <div className="text-xs font-semibold text-emerald-900">🔗 Paste this webhook URL in the Razorpay dashboard:</div>
          <CopyBox value={cfg.webhook_url} testid="rzp-webhook-url" />
          <div className="text-[11px] text-slate-600">Enable events: <code>payment.captured, payment.failed, payment_link.paid, payment_link.expired, refund.processed</code></div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <div className="text-sm font-semibold flex items-center gap-2"><KeyRound size={14} /> Swap & save credentials</div>
        <SwapField label="Key ID" current={cfg.key_id_masked} placeholder="rzp_test_xxx / rzp_live_xxx" value={form.key_id} onChange={(v) => setForm({ ...form, key_id: v })} testid="rzp-key-id" />
        <SwapField label="Key Secret" current={cfg.key_secret_masked} placeholder="paste new key secret" value={form.key_secret} onChange={(v) => setForm({ ...form, key_secret: v })} secret testid="rzp-key-secret" />
        <SwapField label="Webhook Secret (must match dashboard)" current={cfg.webhook_secret_masked} placeholder="paste new webhook secret" value={form.webhook_secret} onChange={(v) => setForm({ ...form, webhook_secret: v })} secret testid="rzp-webhook-secret" />
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={busy} data-testid="rzp-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Razorpay
          </Button>
          <Button size="sm" variant="outline" onClick={syncMethods} disabled={syncing} data-testid="rzp-sync-methods">
            {syncing ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Auto-sync Payment Methods
          </Button>
        </div>
        <div className="text-[11px] text-slate-500">
          Pulls UPI / Card / Netbanking / Wallet / EMI from Razorpay for every paid order and fills the Sales Report + TDS Report automatically. Run this once now, and every future payment fills itself via the webhook.
        </div>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Active Payment Gateway Switcher                                      */
/* ------------------------------------------------------------------ */
function PaymentGatewaySwitch() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setState((await api.get(`/integrations/payment-gateway`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flip = async (gw) => {
    if (!state || state.active === gw) return;
    setBusy(true);
    try {
      await api.post(`/integrations/payment-gateway`, { gateway: gw });
      toast.success(`Active gateway → ${gw.toUpperCase()}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Switch failed"); }
    finally { setBusy(false); }
  };
  if (!state) return null;
  const chip = (gw, ready) => {
    const active = state.active === gw;
    return (
      <button
        type="button"
        data-testid={`gw-pick-${gw}`}
        disabled={!ready || busy}
        onClick={() => flip(gw)}
        className={`flex-1 rounded-xl border px-4 py-3 text-left transition ${
          active
            ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/40"
            : ready
              ? "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40"
              : "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
        }`}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{gw === "razorpay" ? "Razorpay" : "Cashfree"}</span>
          {active && <Badge className="bg-emerald-600 text-white">Active</Badge>}
          {!ready && <Badge className="bg-amber-100 text-amber-800">Not configured</Badge>}
        </div>
        <div className="text-[11px] text-slate-500 mt-1">
          {gw === "razorpay"
            ? "Standard cards / UPI / netbanking — best in India for online orders."
            : "Payment links + hosted forms — currently powers the FBC card form."}
        </div>
      </button>
    );
  };
  return (
    <Card><CardContent className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ArrowLeftRight size={16} className="text-emerald-700" />
        <h2 className="text-lg font-semibold">Active Payment Gateway</h2>
        <span className="text-xs text-muted-foreground ml-auto">
          Applies to <b>new</b> payment links only. Already-issued links keep working on whichever gateway created them.
        </span>
      </div>
      <div className="flex gap-3">
        {chip("razorpay", state.razorpay_ready)}
        {chip("cashfree", state.cashfree_ready)}
      </div>
    </CardContent></Card>
  );
}


/* ------------------------------------------------------------------ */
/* Cashfree                                                            */
/* ------------------------------------------------------------------ */
function CashfreePanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "", webhook_secret: "", env: "", api_version: "" });
  const [busy, setBusy] = useState(false);
  const [pinging, setPinging] = useState(false);
  const load = useCallback(async () => {
    try { setCfg((await api.get(`/integrations/cashfree`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const patch = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
    if (!Object.keys(patch).length) { toast.error("Nothing to save — paste a new value first"); return; }
    setBusy(true);
    try {
      await api.post(`/integrations/cashfree`, patch);
      toast.success("Cashfree updated ✓");
      setForm({ client_id: "", client_secret: "", webhook_secret: "", env: "", api_version: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const ping = async () => {
    setPinging(true);
    try {
      const { data } = await api.post(`/integrations/cashfree/ping`, {});
      // v1.54 — friendly summary of the deep probe.
      if (data.ok) {
        toast.success(`Cashfree OK · ${data.env} · link creation works ✅`, { duration: 8000 });
      } else if (data.link_probe?.error) {
        const err = data.link_probe.error;
        const t = data.link_probe.type ? ` (${data.link_probe.type})` : "";
        toast.error(`Cashfree link creation failed: ${err}${t}`, { duration: 15000 });
      } else if (data.auth_probe?.status && data.auth_probe.status >= 400 && data.auth_probe.status !== 404) {
        toast.error(`Cashfree auth failed · HTTP ${data.auth_probe.status} · ${(data.auth_probe.body || "").slice(0, 180)}`, { duration: 15000 });
      } else if (data.auth_probe?.error) {
        toast.error(`Cashfree unreachable: ${data.auth_probe.error}`, { duration: 12000 });
      } else {
        toast.error(`Ping inconclusive · status ${data.auth_probe?.status || "?"} · ${(data.link_probe?.body || "").slice(0, 180)}`, { duration: 12000 });
      }
      console.log("Cashfree ping result:", data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Ping failed"); }
    finally { setPinging(false); }
  };
  if (!cfg) return <Card><CardContent className="p-4">Loading Cashfree…</CardContent></Card>;
  return (
    <div className="space-y-4">
      <PaymentGatewaySwitch />
      <Card><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Wallet className="text-indigo-700" />
          <h2 className="text-lg font-semibold">Cashfree Payments</h2>
          <Badge className={cfg.client_id_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{cfg.client_id_configured ? "Configured" : "Not configured"}</Badge>
          <Badge className="bg-slate-100 text-slate-700 uppercase">{cfg.env}</Badge>
          <a className="ml-auto text-xs text-primary underline flex items-center gap-1" href="https://merchant.cashfree.com/merchants/pg/developers/webhooks" target="_blank" rel="noreferrer"><ExternalLink size={11} /> Cashfree dashboard</a>
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
          <div className="text-xs font-semibold text-indigo-900">🔗 Paste this webhook URL in the Cashfree dashboard:</div>
          <CopyBox value={cfg.webhook_url} testid="cf-webhook-url" />
          <div className="text-[11px] text-slate-600">Enable events: <code>PAYMENT_SUCCESS_WEBHOOK, PAYMENT_FAILED_WEBHOOK, PAYMENT_LINK_EVENT, REFUND_STATUS_WEBHOOK</code></div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <div className="text-sm font-semibold flex items-center gap-2"><KeyRound size={14} /> Swap & save credentials</div>
        <SwapField label="Client ID" current={cfg.client_id_masked} placeholder="paste Cashfree Client ID" value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} testid="cf-client-id" />
        <SwapField label="Client Secret" current={cfg.client_secret_masked} placeholder="paste Cashfree Client Secret" value={form.client_secret} onChange={(v) => setForm({ ...form, client_secret: v })} secret testid="cf-client-secret" />
        <SwapField label="Webhook Secret (optional — defaults to Client Secret)" current={cfg.webhook_secret_masked} placeholder="only if your dashboard exposes a separate one" value={form.webhook_secret} onChange={(v) => setForm({ ...form, webhook_secret: v })} secret testid="cf-webhook-secret" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] uppercase text-slate-500 mb-1">Environment</div>
            <select className="h-10 w-full rounded-lg border px-3 text-sm" value={form.env} onChange={(e) => setForm({ ...form, env: e.target.value })} data-testid="cf-env">
              <option value="">(no change — currently {cfg.env})</option>
              <option value="sandbox">sandbox</option>
              <option value="production">production</option>
            </select>
          </div>
          <div>
            <SwapField label="API Version" current={cfg.api_version} placeholder="2025-01-01" value={form.api_version} onChange={(v) => setForm({ ...form, api_version: v })} testid="cf-api-version" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={busy} data-testid="cf-save" className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Cashfree
          </Button>
          <Button size="sm" variant="outline" onClick={ping} disabled={pinging} data-testid="cf-ping">
            {pinging ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Test connection
          </Button>
        </div>
      </CardContent></Card>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Delhivery                                                           */
/* ------------------------------------------------------------------ */
function DelhiveryPanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ api_token: "", client_name: "", pickup_pin: "", env: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setCfg((await api.get(`/integrations/delhivery`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const patch = Object.fromEntries(Object.entries(form).filter(([, v]) => v));
    if (!Object.keys(patch).length) { toast.error("Nothing to save"); return; }
    setBusy(true);
    try {
      await api.post(`/integrations/delhivery`, patch);
      toast.success("Delhivery updated ✓");
      setForm({ api_token: "", client_name: "", pickup_pin: "", env: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!cfg) return <Card><CardContent className="p-4">Loading Delhivery…</CardContent></Card>;
  return (
    <div className="space-y-4">
      <Card><CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Truck className="text-sky-700" />
          <h2 className="text-lg font-semibold">Delhivery (B2C shipping)</h2>
          <Badge className={cfg.api_token_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{cfg.api_token_configured ? "Configured" : "Not configured"}</Badge>
          <Badge variant="outline" className="text-[10px]">{cfg.env}</Badge>
          {cfg.auto_ship_on_dispatch && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]"><CheckCircle2 size={10} className="mr-1" /> Auto-ship on dispatch</Badge>}
        </div>
        <p className="text-xs text-slate-600">Order status → <b>dispatched</b> in the CRM triggers a Delhivery API call automatically. AWB attaches back to the order.</p>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <div className="text-sm font-semibold flex items-center gap-2"><KeyRound size={14} /> Swap & save credentials</div>
        <SwapField label="API Token" current={cfg.api_token_masked} placeholder="paste Delhivery API token" value={form.api_token} onChange={(v) => setForm({ ...form, api_token: v })} secret testid="dh-token" />
        <SwapField label="Client Name" current={cfg.client_name} placeholder="MEDISHIV-B2C" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} testid="dh-client" />
        <SwapField label="Pickup PIN" current={cfg.pickup_pin} placeholder="6-digit pickup PIN" value={form.pickup_pin} onChange={(v) => setForm({ ...form, pickup_pin: v })} testid="dh-pin" />
        <div className="space-y-1">
          <div className="text-[11px] uppercase text-slate-500 flex items-center gap-2"><KeyRound size={11} /> Environment</div>
          <select className="h-10 border rounded-lg px-3 text-sm w-full" value={form.env} onChange={(e) => setForm({ ...form, env: e.target.value })} data-testid="dh-env">
            <option value="">Keep current: {cfg.env}</option>
            <option value="staging">staging</option>
            <option value="production">production</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={busy} data-testid="dh-save" className="bg-sky-600 hover:bg-sky-700 text-white">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save Delhivery
          </Button>
          <Button size="sm" variant="outline" data-testid="dh-ping-btn" onClick={async () => {
            setBusy(true);
            try {
              const { data } = await api.post(`/integrations/delhivery/ping`);
              if (data?.ok) toast.success(`Delhivery reachable — HTTP ${data.status} (${data.env})`);
              else toast.error(`Delhivery ping failed: ${data?.reason || `HTTP ${data?.status}`}`);
            } catch (e) { toast.error(e?.response?.data?.detail || "Ping failed"); }
            finally { setBusy(false); }
          }}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} Test connectivity
          </Button>
        </div>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Connect One                                                         */
/* ------------------------------------------------------------------ */
function ConnectOnePanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ external_api_token: "", push_webhook_url: "", push_webhook_secret: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setCfg((await api.get(`/integrations/connect-one`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (extra = {}) => {
    const patch = { ...extra, ...Object.fromEntries(Object.entries(form).filter(([, v]) => v)) };
    if (!Object.keys(patch).length) { toast.error("Nothing to save"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/connect-one`, patch);
      toast.success("Connect One updated ✓");
      if (data.new_token_full) toast.success(`New token: ${data.new_token_full}`, { duration: 30000 });
      setForm({ external_api_token: "", push_webhook_url: "", push_webhook_secret: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  if (!cfg) return <Card><CardContent className="p-4">Loading Connect One…</CardContent></Card>;
  return (
    <div className="space-y-4">
      <Card><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Link2 className="text-fuchsia-700" />
          <h2 className="text-lg font-semibold">Connect One (partner accounting app)</h2>
          <Badge className={cfg.token_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{cfg.token_configured ? "Token active" : "No token"}</Badge>
        </div>
        <p className="text-xs text-slate-600">Feed BK-Teacher payment invoices + user details to your Connect One accounting app. Choose PULL (they poll us) or PUSH (we POST to them) — or both.</p>

        <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/50 p-3 space-y-2">
          <div className="text-xs font-semibold text-fuchsia-900">📥 PULL — Connect One polls these URLs:</div>
          <div className="text-[10px] uppercase text-slate-500">JSON</div>
          <CopyBox value={cfg.pull_url_json} testid="c1-pull-json" />
          <div className="text-[10px] uppercase text-slate-500">CSV</div>
          <CopyBox value={cfg.pull_url_csv} testid="c1-pull-csv" />
          <div className="text-[11px] text-slate-600">Auth header: <code>Authorization: Bearer &lt;token&gt;</code></div>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <div className="text-sm font-semibold">🔑 API token</div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Current:</span>
          <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">{cfg.token_masked || "—"}</span>
        </div>
        <SwapField label="Set a specific token" current={cfg.token_masked} placeholder="paste token here" value={form.external_api_token} onChange={(v) => setForm({ ...form, external_api_token: v })} secret testid="c1-token" />
        <div className="flex gap-2">
          <Button size="sm" onClick={() => save()} disabled={busy} data-testid="c1-save-token" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save token
          </Button>
          <Button size="sm" variant="outline" onClick={() => save({ regenerate_token: true })} disabled={busy} data-testid="c1-regen-token">
            <RefreshCw size={14} /> Regenerate token
          </Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3">
        <div className="text-sm font-semibold">📤 PUSH — we POST to your Connect One webhook</div>
        <SwapField label="Connect One inbound webhook URL" current={cfg.push_webhook_url} placeholder="https://connectone.example/api/medishiv/inbound" value={form.push_webhook_url} onChange={(v) => setForm({ ...form, push_webhook_url: v })} testid="c1-push-url" />
        <SwapField label="Shared secret (X-Medishiv-Signature)" current={cfg.push_webhook_secret_masked} placeholder="paste shared secret" value={form.push_webhook_secret} onChange={(v) => setForm({ ...form, push_webhook_secret: v })} secret testid="c1-push-secret" />
        <Button size="sm" onClick={() => save()} disabled={busy} data-testid="c1-save-push" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save push webhook
        </Button>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Integrations page — with embedded Sheets / Reports / Export   */
/* ------------------------------------------------------------------ */
export default function Integrations() {
  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Plug /> Integrations & Data</h1>
        <p className="text-sm text-muted-foreground">Every third-party service lives here — swap the token or key and hit save, WhatsApp-style. Also includes Google Sheets, Reports, and Data Export.</p>
      </div>
      <Tabs defaultValue="razorpay" className="space-y-3">
        <TabsList className="flex-wrap bg-slate-100">
          <TabsTrigger value="razorpay" data-testid="int-tab-razorpay"><CreditCard size={14} className="mr-1.5" /> Razorpay</TabsTrigger>
          <TabsTrigger value="cashfree" data-testid="int-tab-cashfree"><Wallet size={14} className="mr-1.5" /> Cashfree</TabsTrigger>
          <TabsTrigger value="delhivery" data-testid="int-tab-delhivery"><Truck size={14} className="mr-1.5" /> Delhivery</TabsTrigger>
          <TabsTrigger value="connectone" data-testid="int-tab-connectone"><Link2 size={14} className="mr-1.5" /> Connect One</TabsTrigger>
          <TabsTrigger value="bkroster" data-testid="int-tab-bkroster"><ShieldCheck size={14} className="mr-1.5" /> BK Insurance Roster</TabsTrigger>
          <TabsTrigger value="bkcentres" data-testid="int-tab-bkcentres"><Building2 size={14} className="mr-1.5" /> BK Centre UIDs</TabsTrigger>
          <TabsTrigger value="sheets" data-testid="int-tab-sheets"><SheetIcon size={14} className="mr-1.5" /> Google Sheets</TabsTrigger>
          <TabsTrigger value="reports" data-testid="int-tab-reports"><BarChart3 size={14} className="mr-1.5" /> Reports</TabsTrigger>
          <TabsTrigger value="export" data-testid="int-tab-export"><Download size={14} className="mr-1.5" /> Data Export</TabsTrigger>
        </TabsList>

        <TabsContent value="razorpay"><RazorpayPanel /></TabsContent>
        <TabsContent value="cashfree"><CashfreePanel /></TabsContent>
        <TabsContent value="delhivery"><DelhiveryPanel /></TabsContent>
        <TabsContent value="connectone"><ConnectOnePanel /></TabsContent>
        <TabsContent value="bkroster"><BkRosterPanel /></TabsContent>
        <TabsContent value="bkcentres"><BkCentresPanel /></TabsContent>
        <TabsContent value="sheets" className="-mx-6 -mt-3"><Sheets /></TabsContent>
        <TabsContent value="reports" className="-mx-6 -mt-3"><Reports /></TabsContent>
        <TabsContent value="export" className="-mx-6 -mt-3"><ExportPage /></TabsContent>
      </Tabs>
    </div>
  );
}
